import React, { useRef, useState, useEffect } from 'react';
import { guestBooks } from '../data/guestBooks';
import BookCard from './BookCard';

// Clean, elegant botanical arrow specifically designed for navigation
const BotanicalArrow = ({ direction = "right", className = "" }) => {
  const isLeft = direction === "left";
  
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${isLeft ? 'rotate-180' : ''} ${className}`}
      aria-hidden="true"
    >
      {/* 
        Container group handles the subtle "reaching" animation.
        Respects prefers-reduced-motion to prevent unwanted animation.
      */}
      <g className="transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:translate-x-0.5">
        {/* Elegant curved branch shaft */}
        <path d="M4 12 Q 12 14.5 20 12" />
        
        {/* Clean, immediately recognizable directional tip */}
        <path d="M14 6 L 20 12 L 14 18" />
        
        {/* Top Leaf (Subtle rotation on hover) */}
        <path 
          d="M10.5 11 Q 8.5 6.5 6.5 8 Q 9 10.5 10.5 11 Z" 
          fill="currentColor" 
          stroke="none"
          className="origin-[10.5px_11px] transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:rotate-[15deg]"
        />
        
        {/* Bottom Leaf (Subtle opposing rotation on hover) */}
        <path 
          d="M8.5 13.5 Q 11 18 12.5 16 Q 10.5 14.5 8.5 13.5 Z" 
          fill="currentColor" 
          stroke="none"
          className="origin-[8.5px_13.5px] transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:-rotate-[15deg]"
        />
      </g>
    </svg>
  );
};

export default function GuestBooksSlider() {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  // Only render records where published is true
  const publishedBooks = guestBooks.filter((book) => book.published);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 1);

      // Determine active dot index based on scroll position
      const cardWidth = scrollWidth / publishedBooks.length;
      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(index, publishedBooks.length - 1));
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Scroll by mostly one container width to keep context
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (publishedBooks.length === 0) return null;

  return (
    <section id="guest-books" className="py-24 bg-warm-cream relative overflow-hidden">
      {/* Decorative Background Overlay (Subtle White Wash) */}
      <div 
        className="absolute inset-0 opacity-50 pointer-events-none" 
        style={{ background: 'radial-gradient(circle at top center, #FFFFFF 0%, transparent 80%)' }} 
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Introduction */}
        <div className="text-center mb-16">
          <span className="text-soft-gold font-semibold tracking-[0.15em] uppercase text-sm block mb-4">
            From Our Podcast Guests
          </span>
          <h2 className="text-4xl md:text-5xl text-primary-green font-serif mb-6 leading-tight">
            The Conversation Continues
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Explore books by Janet’s guests and go deeper into their stories of adoption, identity, family, and healing.
          </p>
        </div>

        {/* Slider Area */}
        <div className="relative group/slider">
          {/* Left Arrow Controls (Hidden on mobile where swiping is natural) */}
          <button 
            type="button"
            onClick={() => scroll('left')} 
            disabled={!canScrollLeft}
            aria-label="Scroll left"
            className={`group hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 lg:-translate-x-8 z-10 p-3.5 rounded-full bg-white shadow-[0_4px_14px_rgba(0,0,0,0.06)] border border-[#F0EFEA] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-soft-gold ${
              !canScrollLeft 
                ? 'opacity-0 pointer-events-none' 
                : 'opacity-100 hover:bg-warm-cream text-forest-green hover:text-soft-gold hover:-translate-x-6 lg:hover:-translate-x-9'
            }`}
          >
            <BotanicalArrow direction="left" />
          </button>

          {/* Slider Track */}
          <ul 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pt-4 px-2 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {publishedBooks.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </ul>

          {/* Right Arrow Controls */}
          <button 
            type="button"
            onClick={() => scroll('right')} 
            disabled={!canScrollRight}
            aria-label="Scroll right"
            className={`group hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 lg:translate-x-8 z-10 p-3.5 rounded-full bg-white shadow-[0_4px_14px_rgba(0,0,0,0.06)] border border-[#F0EFEA] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-soft-gold ${
              !canScrollRight 
                ? 'opacity-0 pointer-events-none' 
                : 'opacity-100 hover:bg-warm-cream text-forest-green hover:text-soft-gold hover:translate-x-6 lg:hover:translate-x-9'
            }`}
          >
            <BotanicalArrow direction="right" />
          </button>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2.5 mt-2" aria-hidden="true">
          {publishedBooks.map((_, idx) => (
            <div 
              key={idx} 
              className={`h-2 rounded-full transition-all duration-500 ease-out ${
                idx === activeIndex ? 'w-8 bg-soft-gold' : 'w-2.5 bg-[#E2DFD3]'
              }`} 
            />
          ))}
        </div>

        {/* Affiliate Disclosure */}
        <div className="mt-16 text-center">
          <p className="text-[14px] text-gray-500 italic tracking-wide">
            As an Amazon Associate, we earn from qualifying purchases.
          </p>
        </div>
      </div>
    </section>
  );
}